import type { ForwardedRef, InputHTMLAttributes } from 'react';
import type { TColours } from '~/utils/types.ts';
import { forwardRef } from 'react';

type TDefaultFormType = InputHTMLAttributes<HTMLInputElement> & {
	id: string;
	label: string;
	name: string;
	actionData: string | number | undefined;
	defaultValue: string | number | undefined;
	isRequired?: boolean;
	helper?: JSX.Element | undefined;
	autocomplete?: string;
	autofocus?: boolean;
};

type TInputType = TDefaultFormType & {
	type: string;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};
export const Input = forwardRef(
	(
		{
			id,
			label,
			type,
			name,
			actionData,
			defaultValue,
			isRequired = false,
			helper,
			autocomplete,
			autofocus,
			onBlur,
		}: TInputType,
		ref: ForwardedRef<any>,
	) => {
		return (
			<div>
				<label htmlFor={id}>{label}</label>
				<input
					type={type}
					id={id}
					defaultValue={defaultValue}
					name={name}
					aria-invalid={Boolean(actionData) || undefined}
					aria-errormessage={actionData ? `${id}-error` : undefined}
					required={isRequired}
					autoComplete={autocomplete}
					onBlur={onBlur}
					ref={ref}
					data-autofocus={autofocus}
				/>
				{actionData ? (
					<p
						className="form-validation-error"
						role="alert"
						id={`${id}-error`}
					>
						{actionData}
					</p>
				) : null}
				{helper ? helper : null}
			</div>
		);
	},
);
Input.displayName = 'Input';

type TTextareaType = TDefaultFormType & {
	rows?: number | undefined;
};
export const Textarea = ({
	id,
	label,
	name,
	actionData,
	rows = 3,
	defaultValue,
	isRequired = false,
	helper,
}: TTextareaType) => {
	return (
		<div>
			<label htmlFor={id}>{label}</label>
			<textarea
				defaultValue={defaultValue}
				id={id}
				name={name}
				rows={rows}
				aria-invalid={Boolean(actionData) || undefined}
				aria-errormessage={actionData ? 'description-error' : undefined}
				required={isRequired}
			/>
			{actionData ? (
				<p
					className="form-validation-error"
					role="alert"
					id="description-error"
				>
					{actionData}
				</p>
			) : null}
			{helper ? helper : null}
		</div>
	);
};

type TCheckboxType = {
	id: string;
	label: string;
	name: string;
	value: string | undefined;
	defaultChecked: boolean | undefined;
	isRequired?: boolean;
};
export const Checkbox = ({
	id,
	label,
	name,
	value,
	defaultChecked,
	isRequired = false,
}: TCheckboxType) => {
	return (
		<div className="c-checkbox">
			<input
				type="checkbox"
				id={id}
				name={name}
				value={value}
				className="sr-only"
				defaultChecked={defaultChecked}
				required={isRequired}
			/>
			<label htmlFor={id} className="form-checkbox c-checkbox--label">
				<svg viewBox="0 0 100 100">
					<path
						className="path"
						fill="none"
						stroke="#000"
						strokeWidth="13"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeMiterlimit="10"
						d="M12.1 52.1l24.4 24.4 53-53"
					/>
				</svg>
				{label}
			</label>
		</div>
	);
};

type TColourSelectType = TDefaultFormType & {
	colours: TColours;
};
export const ColourSelect = ({
	id,
	label,
	name,
	actionData,
	defaultValue,
	colours,
	isRequired = false,
}: TColourSelectType) => {
	return (
		<div>
			<label htmlFor={id}>{label}</label>
			<select
				id={id}
				name={name}
				defaultValue={defaultValue}
				aria-invalid={Boolean(actionData) || undefined}
				aria-errormessage={actionData ? 'colour-error' : undefined}
				required={isRequired}
			>
				<option value="">Default</option>
				{Object.entries(colours).map(([strKey, objColour]) => (
					<option key={objColour.name} value={strKey}>
						{objColour.name}
					</option>
				))}
			</select>
			{actionData ? (
				<p
					className="form-validation-error"
					role="alert"
					id="colour-error"
				>
					{actionData}
				</p>
			) : null}
		</div>
	);
};

export const GroupType = ({
	id,
	label,
	name,
	actionData,
	defaultValue,
	isRequired = false,
}: TDefaultFormType) => {
	return (
		<div>
			<label htmlFor={id}>{label}</label>
			<select
				id={id}
				name={name}
				defaultValue={defaultValue}
				aria-invalid={Boolean(actionData) || undefined}
				aria-errormessage={actionData ? 'group-type-error' : undefined}
				required={isRequired}
			>
				<option value="gift">Gift/Family</option>
				<option value="todo">Simple To Do</option>
				<option value="holiday">Holiday Planner</option>
				<option value="music">Music/Playlist</option>
			</select>
			{actionData ? (
				<p
					className="form-validation-error"
					role="alert"
					id="colour-error"
				>
					{actionData}
				</p>
			) : null}
		</div>
	);
};
